import { useState } from 'react'
import { Search, Trash2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { getTagSummary, triggerTagCleanup } from '@/lib/api'

export default function Tags() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tags', 'summary'],
    queryFn: getTagSummary,
    refetchInterval: 60_000,
  })

  const cleanupMutation = useMutation({
    mutationFn: triggerTagCleanup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })

  const tags = data?.tags || []
  const searchTerm = search.trim().toLowerCase()
  const filteredTags = tags.filter(({ tag }) => tag.toLowerCase().includes(searchTerm))

  return (
    <div className="flex flex-col">
      <Header title="Tags" showSearch={false} />

      <div className="w-full max-w-5xl flex-1 space-y-6 p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tags</CardTitle>
            <CardDescription>
              Browse tags and the number of coins using each one. Manage a coin’s tags in its ticker details on Coin.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-0 flex-1 basis-48">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search tags"
                  placeholder="Search tags..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => cleanupMutation.mutate()}
                disabled={cleanupMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Cleanup Expired
              </Button>
            </div>

            {cleanupMutation.isError && (
              <p role="alert" className="text-sm text-destructive">Could not clean up expired tags. Please try again.</p>
            )}

            {isLoading ? (
              <div role="status" className="py-8 text-center text-muted-foreground">Loading tags...</div>
            ) : isError ? (
              <div role="alert" className="space-y-3 py-8 text-center">
                <p className="text-muted-foreground">Could not load tags.</p>
                <Button variant="outline" onClick={() => void refetch()}>Retry</Button>
              </div>
            ) : (
              <>
                <p role="status" className="text-sm text-muted-foreground">
                  {searchTerm ? `${filteredTags.length} of ${tags.length} tags` : `${tags.length} ${tags.length === 1 ? 'tag' : 'tags'}`}
                </p>
                {filteredTags.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    {searchTerm ? 'No tags match your search' : 'No tags configured'}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <table className="w-full table-fixed text-sm" aria-label="Tags and coin counts">
                      <thead className="border-b bg-muted/40 text-muted-foreground">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left font-medium">Tag</th>
                          <th scope="col" className="w-24 px-4 py-3 text-right font-medium">Coins</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredTags.map(({ tag, coin_count }) => (
                          <tr key={tag}>
                            <td className="px-4 py-3">
                              <Badge variant="secondary" className="max-w-full break-words [overflow-wrap:anywhere]">{tag}</Badge>
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">{coin_count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
